import {
  useFetchListOrderReady,
  useFetchListInprogress,
  useFetchListOnHold,
  useFetchListFinished,
  useFetchListByStatusSlug,
  useFetchListRTO,
  useFetchOrderEnRoute,
  useFetchOrderCollecting,
  useFetchReturningOrigin,
  useFetchReturningWarehouse,
  useFetchActionRequiredOrderList,
  useFetchListRtoRequest,
} from '@/hooksApi/useOrder';
import { OrderStatusTab } from '@/types/columns.ts';
import { OrderListResponse, Product } from '@/types';
import { formatDateByDayjs, getOrderParams, getOrderPickupAddress } from '@/utils';
import useSearchStore, { ISearch } from './useSearchStore';
import { UseQueryResult } from '@tanstack/react-query';
import { MerchantTitleOrderStatusEnum, OrderStatusEnum } from '@/config';
import { OrderFilters } from '@/stores/useFilterOrderStore';
import { extractAppliedRule } from '@/utils/extractAppliedRule';

export interface OrderStatus {
  slug: OrderStatusEnum;
  merchantTitle: MerchantTitleOrderStatusEnum;
}
export interface OrderDataType {
  key: string;
  orderDate: string;
  orderDateRaw: string;
  // FE-2: formatted date when this order was first marked for return.
  // null for non-RTO orders.
  rtoRequestedAt?: string | null;
  orderId: string;
  customerName?: string;
  customerAddress?: string;
  orderValue: number;
  platform_code: string;
  custom_id: string;
  status: OrderStatus;
  // True when the order was collected via a BULK pickup. Used to render
  // "Bulk Collected" instead of "Collected" while status.slug === 'collected'.
  is_bulk_collected?: boolean;
  // TEMP-9: cold-chain flag — renders the Snowflake pill in the list.
  temperature_control?: boolean;
  // One-way "a label was generated" flag — drives the Reprint swap + Printed pill.
  label_printed?: boolean;
  pickupLocation: string;
  pickupAddressId: string | null;
  products: Product[];
  driver: {
    phone: string | undefined;
    assign: boolean;
  };
  merchant_action_required: number;
  merchant_action_required_text: string;
  carrier_message: string;
  phone: string;
  // Item 2 — rule attribution from the merchant order payload.
  // `applied_rule_uid` / `applied_rule_name` identify the ShippingRule that
  // ACTUALLY assigned the order's carrier. Both are null when the merchant
  // overrode the carrier (no rule attribution) — the UI then shows nothing.
  applied_rule_uid?: string | null;
  applied_rule_name?: string | null;
}

type FetchDataFunction = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => UseQueryResult<OrderListResponse, Error>;

const useOrderData = (
  current: number,
  activeTab: OrderStatusTab,
  activeButton: string,
  pageSize: number,
  orderFilters: OrderFilters,
) => {
  const { searchValue: searchTermHeader, setSearchValue: setSearchTermHeader } = useSearchStore();

  const apiCallEnabled =
    //  orderFilters.searchValue?.searchBy
    //   ? !!orderFilters.searchValue?.search
    //   :

    true;

  //define API call fo each tab
  const useFetchListData: {
    [key in OrderStatusTab]: Record<string, FetchDataFunction>;
  } = {
    [OrderStatusTab.READY_TO_SHIP]: { ['all']: useFetchListOrderReady },
    [OrderStatusTab.IN_PROGRESS]: {
      ['all']: useFetchListInprogress,
      ['en-route']: useFetchOrderEnRoute,
      ['collecting']: useFetchOrderCollecting,
    },
    [OrderStatusTab.ON_HOLD]: {
      ['all']: useFetchListOnHold,
      ['action-required']: useFetchActionRequiredOrderList,
    },
    [OrderStatusTab.RTO]: {
      ['all']: useFetchListRTO,
      ['rto-requested']: useFetchListRtoRequest,
      ['rto-in-transit']: useFetchReturningOrigin,
      ['rto-received-warehouse']: useFetchReturningWarehouse,
    },
    [OrderStatusTab.INVENTORY]: { ['all']: useFetchListFinished },
    [OrderStatusTab.FINISHED]: { ['all']: useFetchListFinished },
  };

  const filterParams = getOrderParams(orderFilters);

  // get order list using status slug(used for some of tabs)
  const fetchWithStatusSlug = (
    page: number,
    pageSize?: number,
    enabled?: boolean,
    search?: ISearch,
  ) => {
    return useFetchListByStatusSlug(activeButton, page, pageSize, search, filterParams);
  };

  // if selectedTab has it own API use it. else=> use fetchWithStatusSlug
  const queryFunction = useFetchListData[activeTab][activeButton] || fetchWithStatusSlug;
  const { data, isLoading, isFetching, refetch } = queryFunction(
    current,
    pageSize,
    apiCallEnabled,
    searchTermHeader,
    // orderFilters.searchValue,
    filterParams,
  );

  // transform data
  const mapOrderData = (data: OrderListResponse | undefined): OrderDataType[] => {
    if (!data?.object) return [];
    return (
      data?.object?.items?.map((item) => ({
        key: item.uid,
        orderDate: formatDateByDayjs(item.created_at, 'D MMMM YYYY'),
        orderDateRaw: item.created_at,
        // FE-2: surface the "Return requested" timestamp on the RTO tab. Accepts
        // either the appended `rto_requested_at` string from /orders/rto (list
        // endpoint) or the same field from /orders/search (Meilisearch path).
        rtoRequestedAt: (() => {
          const flat = (item as any)?.rto_requested_at;
          if (flat) return formatDateByDayjs(flat, 'D MMMM YYYY');
          const rtoStatus = (item as any)?.statuses?.find(
            (s: any) => s?.slug === 'rto-requested' || s?.slug === 'rto-request-pending',
          );
          const ts = rtoStatus?.pivot?.created_at;
          return ts ? formatDateByDayjs(ts, 'D MMMM YYYY') : null;
        })(),
        orderId: item?.platform_code,
        customerName: item?.full_name,
        customerAddress: item?.address_text,
        orderValue: item?.products_value,
        platform_code: item?.platform_code,
        custom_id: item?.custom_id || '---',
        status: {
          slug: (item?.status?.slug as OrderStatusEnum) || '',
          merchantTitle: (item?.status?.merchant_title as MerchantTitleOrderStatusEnum) ?? '',
        },
        is_bulk_collected: item?.is_bulk_collected ?? false,
        // TEMP-9: thread the cold-chain flag so the list can badge the order.
        temperature_control: item?.temperature_control ?? false,
        label_printed: item?.label_printed ?? false,
        // ! We need slug to realize the exact status of the order (not merchant_title) for checking "inProgressActionsByStatus".
        carrier_message: item?.carrier_message || '---',
        pickupLocation: getOrderPickupAddress(item)?.title || '---',
        pickupAddressId: getOrderPickupAddress(item)?.uid || '',
        products: item?.products?.map((product) => ({
          name: product?.title,
          quantity: product?.quantity,
          price: product?.price,
        })),
        driver: {
          phone: item?.driver?.phone,
          assign: Boolean(item?.driver) ?? false,
        },
        merchant_action_required: item?.merchant_action_required || 0,
        merchant_action_required_text: item.merchant_action_required_text || '',
        phone: item.phone,
        carrier: item?.carrier,
        ...extractAppliedRule(item as unknown as Record<string, unknown>),
      })) || []
    );
  };

  const totals = isFetching || isLoading ? 0 : data?.object.total || 0;

  const activeData = isFetching || isLoading ? [] : mapOrderData(data);

  return {
    activeData,
    totals,
    isLoading,
    isFetching,
    refetch,
  };
};

export default useOrderData;
